/* eslint-disable import/no-cycle */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { withStyles } from '@glu/theming';
import classnames from 'classnames';
import { Button, TERTIARY } from '@glu/buttons-react';
import withLocale from '../../withLocale/withLocale';
import styles from './PaymentAdditionalDetails.styles';
import AppPropTypes from '../../../proptypes/AppPropTypes';

// components
import Accordion from '../../accordion/Accordion';
import { buildSectionLines } from './paymentAdditionaDetails.utils';
import { getPermissions } from './PaymentAdditionalDetails.permissions';
import BankCodeArea from '../BankCodeArea';
import { getPaymentType } from '../../createPayment/CreatePayment.utils';

export const PaymentAdditionalDetailsComponent = ({
  classes,
  data,
  formChange,
  formState,
  fromTemplate,
  isTemplate,
  locale,
  onLoad,
  preferences,
  type
}) => {
  const [open, toggleOpen] = useState(false);
  const [secondBankVisible, add2ndBank] = useState(false);

  const toggleAccordion = (type) => () => toggleOpen((open) => ({ ...open, [type]: !open[type] }));
  const openSections = () => {
    const sections = {
      interBank: ['intermediaryBankInfo1', 'intermediaryBankInfo2'],
      paymentDetails: ['paymentDetails'],
      bankToBankDetails: ['bankToBankDetails']
    };

    Object.keys(sections).forEach((sec) => {
      const shouldOpen = sections[sec].some(
        (x) => formState[x] && Object.values(formState[x]).filter(Boolean).length && !open[sec]
      );

      if (shouldOpen) {
        toggleAccordion(sec)();
      }
    });
  };

  useEffect(() => {
    if (formState.intermediaryBankInfo2) {
      add2ndBank(true);
    }

    openSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const section = buildSectionLines({
    locale,
    preferences,
    formState,
    paymentType: get(formState, 'paymentType[0].type', formState.paymentType),
    classes,
    onChange: formChange
  });

  const visibility = getPermissions({
    preferences,
    action: 'create-template',
    type: 'intermediary',
    paymentTypes: data.paymentTypeOptions,
    formState,
    isTemplate
  });

  const isInternational =
    get(
      formState,
      'paymentType[0].type',
      getPaymentType(formState, data.paymentTypeOptions, { template: isTemplate })
    ) === 'International';

  return (
    <div className={classnames(classes.root, 'col-xs-12')}>
      <h3 className={classes.header}>{locale['paymentAdditionalDetails.header']}</h3>

      {visibility.intermediary && (
        <Accordion
          open={open.interBank}
          chevron
          panelTitle={`intermediary ${locale['paymentAdditionalDetails.interBank']}`}
          variant="expandable"
          onToggle={toggleAccordion('interBank')}
        >
          <div className={classnames('form-group', classes.row)}>
            <BankCodeArea
              field="intermediaryBankInfo1"
              type={type}
              formState={formState}
              formChange={formChange}
              fromTemplate={fromTemplate}
              locale={locale}
              onLoad={onLoad}
              isTemplate={isTemplate}
              data={data}
            />

            {!secondBankVisible && (
              <Button
                className={classnames('btn btn-tertiary', classes.add2ndBankLink, {
                  [classes.add2ndBankLinkPos]: !isInternational
                })}
                onClick={add2ndBank}
                variant={TERTIARY}
              >
                {locale['paymentAdditionalDetails.add2ndBank']}
              </Button>
            )}

            {secondBankVisible && (
              <BankCodeArea
                field="intermediaryBankInfo2"
                type={type}
                formState={formState}
                formChange={formChange}
                fromTemplate={fromTemplate}
                locale={locale}
                onLoad={onLoad}
                isTemplate={isTemplate}
                data={data}
              />
            )}
          </div>
        </Accordion>
      )}

      <Accordion
        open={open.paymentDetails}
        chevron
        panelTitle={locale['paymentAdditionalDetails.originatorToBeneficiary']}
        variant="expandable"
        onToggle={toggleAccordion('paymentDetails')}
      >
        {section('PD')}
      </Accordion>

      <Accordion
        open={open.bankToBankDetails}
        chevron
        panelTitle={locale['paymentAdditionalDetails.bankToBankInformation']}
        variant="expandable"
        className="panel panel-primary bank-to-bank-details-line-1"
        onToggle={toggleAccordion('bankToBankDetails')}
      >
        {section('BTB')}
      </Accordion>
    </div>
  );
};

PaymentAdditionalDetailsComponent.defaultProps = {
  preferences: {},
  type: '',
  fromTemplate: false
};

PaymentAdditionalDetailsComponent.propTypes = {
  type: PropTypes.string,
  classes: AppPropTypes.jssClasses.isRequired,
  locale: AppPropTypes.locale.isRequired,
  formChange: PropTypes.func.isRequired,
  formState: PropTypes.shape({}).isRequired,
  onLoad: PropTypes.func.isRequired,
  preferences: PropTypes.shape({}),
  fromTemplate: PropTypes.bool,
  data: PropTypes.shape({}).isRequired,
  isTemplate: PropTypes.bool.isRequired
};

const Styled = withStyles(styles)(PaymentAdditionalDetailsComponent);
export default withLocale(Styled);
